import React, { useCallback } from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/cssxp-client-utils';
import { fetchContactDetailsFromContactService } from './queries/fetch-contact-details-from-contact-service';
import { fetchBulkContactDetailsFromContactService } from './queries/fetch-bulk-contact-details-from-contact-service';
import { ContactType, getResourceAri } from './constant';

export const useFetchEccFromContactService = (contactType?: ContactType) => {
  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const defaultContactType = ContactType.CUSTOMER_CONTACT;

  const [fetchEccDetails, { loading: eccDetailsLoading, error: eccDetailsError }] = useLazyQuery(
    fetchContactDetailsFromContactService,
    {
      client: ansClient as any,
    }
  );

  const [fetchBulkEccDetails, { loading: bulkEccDetailsLoading, error: bulkEccDetailsError }] = useLazyQuery(
    fetchBulkContactDetailsFromContactService,
    {
      client: ansClient as any,
    }
  );

  const getECCDetails = useCallback(async (invoiceGroupId: string, transactionAccountId: string) => {
    const { data } = await fetchEccDetails({
      variables: {
        resourceAri: getResourceAri(transactionAccountId, invoiceGroupId),
        contactType: [contactType ?? defaultContactType],
        transactionAccountId: transactionAccountId,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

    return data?.contactsByResource?.filter((contact: any) => contact.accountId && (contact.email || contact.name));
  }, []);

  const getQuotePrimaryCustomerContactDetail = useCallback(
    async ({
      transactionAccountId,
      quoteId,
      contactType,
    }: {
      transactionAccountId: string;
      quoteId?: string;
      contactType?: ContactType;
    }) => {
      const id = quoteId;
      const resourceAri = getResourceAri(transactionAccountId, id as string, ContactType.QUOTE_CONTACT);

      const { data } = await fetchEccDetails({
        variables: {
          resourceAri: resourceAri,
          contactType: [contactType], // this will be CUSTOMER_CONTACT to get the quote level primary customer contact
          transactionAccountId: transactionAccountId,
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      });

      return data?.contactsByResource?.filter(
        (contact: any) => contact.accountId && (contact.email || contact.name) && contact?.isPrimary
      );
    },
    []
  );

  const getECCDetailsForCSV = useCallback(async (invoiceGroupId: string, transactionAccountId: string) => {
    const { data } = await ansClient.query({
      query: fetchContactDetailsFromContactService,
      variables: {
        resourceAri: getResourceAri(transactionAccountId, invoiceGroupId),
        contactType: [ContactType.CUSTOMER_CONTACT],
        transactionAccountId: transactionAccountId,
      },
    });

    return data?.contactsByResource;
  }, []);

  const getBulkECCDetails = useCallback(async (invoiceGroupIds: string[], transactionAccountId: string | undefined) => {
    const resourceAris = invoiceGroupIds?.map((invoiceGroupId) =>
      getResourceAri(transactionAccountId as string, invoiceGroupId, ContactType.CUSTOMER_CONTACT)
    );
    const { data } = await fetchBulkEccDetails({
      variables: {
        resourceAris: resourceAris,
        contactType: [ContactType.CUSTOMER_CONTACT],
        transactionAccountId: transactionAccountId,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

    return data?.bulkContactsByResource;
  }, []);

  return {
    getECCDetails,
    getECCDetailsForCSV,
    eccDetailsLoading,
    eccDetailsError,
    getQuotePrimaryCustomerContactDetail,
    getBulkECCDetails,
    bulkEccDetailsLoading,
    bulkEccDetailsError,
  };
};
